import React, { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { boolean, object, string } from "yup";
import { useTranslation } from "react-i18next";

import LoginForm, { LoginFormValues } from "./LoginForm";

import { useLogin } from "@api";
import { useNavigateAfterLogin } from "@hooks";

const FormContainer: FC = () => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>();
  const navigateAfterLogin = useNavigateAfterLogin();

  const schema = useMemo(
    () =>
      object({
        email: string()
          .email(t("validation.email"))
          .max(255)
          .required(t("validation.required")),
        password: string().max(255).required(t("validation.required")),
        otp: string().when("isOtpRequired", {
          is: true,
          then: (schema) =>
            schema
              .required(t("validation.required"))
              .min(6)
              .max(6, t("validation.otpLength")),
          otherwise: (schema) => schema.notRequired(),
        }),
        isOtpRequired: boolean().required(),
      }),
    [],
  );

  const { control, handleSubmit, formState, setValue, watch } =
    useForm<LoginFormValues>({
      defaultValues: {
        email: "",
        password: "",
        otp: "",
        isOtpRequired: false,
      },
      resolver: yupResolver(schema),
      mode: "onSubmit",
      reValidateMode: "onSubmit",
    });

  const { login, isPendingLogin } = useLogin();

  const onSubmit = (values: LoginFormValues): void => {
    setErrorMessage(undefined);

    login(
      {
        username: values.email,
        password: values.password,
        otp: values.otp || undefined,
      },
      {
        onSuccess: () => {
          navigateAfterLogin();
        },
        onError: (error) => {
          const message = error?.response?.message?.[0];
          const messages: Record<string, string> = {
            invalid_credentials: t("login.invalid_username_or_password"),
            user_blocked: t("login.user_blocked"),
            invalid_otp: t("login.invalid_otp"),
          };

          setErrorMessage(
            message && typeof message === "string"
              ? messages[message] || t("login.invalid_username_or_password")
              : t("login.invalid_username_or_password"),
          );

          if (message === "otp_required") {
            setValue("isOtpRequired", true);
          }
        },
      },
    );
  };

  return (
    <LoginForm
      isOtpRequired={watch("isOtpRequired")}
      onSubmit={handleSubmit(onSubmit)}
      control={control}
      submitting={isPendingLogin}
      isValid={formState.isValid}
      errorMessage={errorMessage}
    />
  );
};

export default FormContainer;
